import axios, { isAxiosError } from "axios";
import { useAuthStore } from "../pages/taskpane/store/authStore";

const api = axios.create({
  baseURL: "http://localhost:8080/api",
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  async (config) => {
    const authStore = useAuthStore.getState();
    
    // Check if token is valid and not expiring soon
    if (authStore.isTokenValid() && !authStore.isTokenExpiringSoon()) {
      const token = authStore.auth.tokens?.accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      }
    }
    
    // Token is expired or expiring soon, try to refresh
    try {
      const newToken = await authStore.refreshTokens();
      if (newToken) {
        config.headers.Authorization = `Bearer ${newToken}`;
      }
    } catch (error) {
      console.error("Failed to refresh token:", error);
      // Continue without token, let the request fail with 401
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (isAxiosError(error) && error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const authStore = useAuthStore.getState();
        const newToken = await authStore.refreshTokens();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        useAuthStore.getState().clearAuth();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

export const getAxiosError = (error: Error | null) => {
  if (!error) return "";
  if (isAxiosError(error)) {
    const e = error.response?.data?.errors;
    if (Array.isArray(e)) {
      return e[0] || "";
    }
    return e;
  }
  return error.message;
};
